import React, { useEffect } from 'react'
import { Line } from 'react-chartjs-2'
import { FiAlertCircle } from 'react-icons/fi'

import { Button } from '@vtex/shoreline'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip
} from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import { t } from 'i18next'

import ChartLegend from '../ChartLegend'
import crosshairPlugin from '../plugins/crosshairPlugin'
import { SpendTooltip } from '../SpendTooltip'

import type { SpendLineChartProps } from './types'
import { Y_AXIS_WIDTH, CHART_HEIGHT, MIN_POINT_WIDTH } from './constants'
import { useSpendLineChart } from './hooks/useSpendLineChart'
import styles from './styles.module.css'

export type { SpendLineChartProps } from './types'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  annotationPlugin,
  crosshairPlugin
)

const SpendLineChart: React.FC<SpendLineChartProps> = props => {
  const {
    error = false,
    errorMessage,
    onRetry,
    labels = [],
    currency = 'BRL',
    elapsedIndex = -1
  } = props

  const {
    chartRef,
    mainScrollRef,
    xAxisScrollRef,
    scrollWrapperRef,
    tooltipData,
    position,
    tooltipRef,
    hiddenKeys,
    legendItems,
    datasets,
    yAxisDatasets,
    xAxisDatasets,
    options,
    yAxisOptions,
    xAxisOptions,
    plugins,
    xAxisPlugins,
    chartMinWidth,
    handleMainScroll,
    handleMouseDown,
    handleMouseMove,
    handleMouseUpOrLeave,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    syncShadows,
    toggleLegend
  } = useSpendLineChart(props)

  useEffect(() => {
    requestAnimationFrame(() => {
      const el = mainScrollRef.current
      if (!el) return
      if (elapsedIndex > 0) {
        el.scrollLeft = el.scrollWidth
        if (xAxisScrollRef.current)
          xAxisScrollRef.current.scrollLeft = el.scrollLeft
      }
      syncShadows(el)
    })
  }, [labels, elapsedIndex, mainScrollRef, xAxisScrollRef, syncShadows])

  return (
    <div className={styles.box}>
      {error ? (
        <div className={styles.errorArea}>
          <FiAlertCircle size={32} color="#707070" />
          <span>{errorMessage ?? 'Não foi possível carregar o gráfico'}</span>
          {!errorMessage && onRetry && (
            <Button onClick={onRetry}>Tentar novamente</Button>
          )}
        </div>
      ) : (
        <>
          <div className={styles.scrollWrapper}>
            <div className={styles.yAxisPanel}>
              <Line
                data={{ labels, datasets: yAxisDatasets }}
                options={yAxisOptions}
                width={Y_AXIS_WIDTH}
                height={CHART_HEIGHT}
              />
            </div>
            <div ref={scrollWrapperRef} className={styles.chartScrollWrapper}>
              <div // NOSONAR
                ref={mainScrollRef}
                role="application"
                aria-label={t('rm:spendReport.chart.ariaLabel')}
                tabIndex={0} // NOSONAR
                className={styles.chartScrollArea}
                onScroll={handleMainScroll}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUpOrLeave}
                onMouseLeave={handleMouseUpOrLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onKeyDown={e => {
                  const el = mainScrollRef.current
                  if (!el) return
                  if (e.key === 'ArrowLeft') el.scrollLeft -= MIN_POINT_WIDTH
                  else if (e.key === 'ArrowRight') el.scrollLeft += MIN_POINT_WIDTH
                }}
              >
                <div
                  className={styles.chartScrollInner}
                  style={{ minWidth: chartMinWidth }}
                >
                  <Line
                    ref={chartRef}
                    data={{ labels, datasets }}
                    options={options}
                    plugins={plugins as never}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.xAxisRow}>
            <div className={styles.xAxisSpacer} />
            <div ref={xAxisScrollRef} className={styles.xAxisScrollArea}>
              <div
                className={styles.xAxisScrollInner}
                style={{ minWidth: chartMinWidth }}
              >
                <Line
                  data={{ labels, datasets: xAxisDatasets }}
                  options={xAxisOptions}
                  plugins={xAxisPlugins as never}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <ChartLegend
        items={legendItems}
        hiddenKeys={hiddenKeys}
        onToggle={toggleLegend}
        className={styles.legend}
      />

      <SpendTooltip
        data={tooltipData}
        position={position}
        tooltipRef={tooltipRef}
        currency={currency}
      />
    </div>
  )
}

export default SpendLineChart
